import React, { useState, useCallback } from 'react'
import {
  Box,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded'
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded'
import GlobalSearchInLawCSP from '../../../../globalSearch/GlobalSearchInLawCSP'
import GlobalSearchInTaxTransparencyCSP from '../../../../globalSearch/GlobalSearchInTaxTransparencyCSP'
import TaxTreatyList from '../../../../taxTreaty/TaxTreatyList'

// Default state of the autocomplete toggle (OFF).
// Admins can flip the in-page Switch to turn autocomplete back on for a session.
const DEFAULT_AUTOCOMPLETE_ENABLED = false

const SUB_TAB_INDEX = {
  TAX_LAWS: 0,
  TAX_TRANSPARENCY: 1,
  TAX_TREATIES: 2,
}

const SUB_TAB_NAMES = ['tax-laws', 'tax-transparency', 'tax-treaties']

const ExactSearchTab = () => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const getInitialSubTab = () => {
    const params = new URLSearchParams(window.location.search)
    const sub = params.get('subtab')
    switch (sub) {
      case 'tax-transparency':
        return SUB_TAB_INDEX.TAX_TRANSPARENCY
      case 'tax-treaties':
        return SUB_TAB_INDEX.TAX_TREATIES
      case 'tax-laws':
      default:
        return SUB_TAB_INDEX.TAX_LAWS
    }
  }

  const [subTabValue, setSubTabValue] = useState(getInitialSubTab())
  const [autocompleteEnabled, setAutocompleteEnabled] = useState(
    DEFAULT_AUTOCOMPLETE_ENABLED,
  )

  const handleSubTabChange = useCallback((event, newValue) => {
    setSubTabValue(newValue)
    const params = new URLSearchParams(window.location.search)
    params.set('subtab', SUB_TAB_NAMES[newValue])
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`)
  }, [])

  const handleAutocompleteToggle = useCallback((event) => {
    setAutocompleteEnabled(event.target.checked)
  }, [])

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 2 },
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
        }}
      >
        <Tabs
          value={subTabValue}
          onChange={handleSubTabChange}
          variant={isSmallScreen ? 'scrollable' : 'standard'}
          scrollButtons={isSmallScreen ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{
            flexGrow: 1,
            minHeight: 48,
            '& .MuiTabs-scroller': {
              overflowX: isSmallScreen ? 'auto !important' : 'hidden',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
              fontWeight: 500,
              minWidth: { xs: 120, sm: 145, md: 0 },
              minHeight: { xs: 72, sm: 76, md: 48 },
              px: { xs: 1.25, sm: 1.75, md: 2 },
              whiteSpace: 'nowrap',
              flexShrink: 0,
            },
            '& .MuiTab-root .MuiSvgIcon-root': {
              fontSize: { xs: '1.15rem', sm: '1.3rem', md: '1.2rem' },
            },
            '& .MuiTab-iconWrapper': {
              marginBottom: { xs: '6px !important', sm: '0 !important', md: '0 !important' },
            },
            '& .MuiTab-root.Mui-selected': {
              color: '#232536',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#232536',
            },
            '& .MuiTabs-scrollButtons': {
              color: '#232536',
              '&.Mui-disabled': {
                opacity: 0.3,
              },
            },
          }}
        >
          <Tab
            icon={<GavelRoundedIcon />}
            iconPosition={isMobile ? 'top' : 'start'}
            label='Tax Laws'
          />
          <Tab
            icon={<ArticleRoundedIcon />}
            iconPosition={isMobile ? 'top' : 'start'}
            label='Tax Transparency'
          />
          <Tab
            icon={<AutoStoriesRoundedIcon />}
            iconPosition={isMobile ? 'top' : 'start'}
            label='Tax Treaties'
          />
        </Tabs>

        <Tooltip
          title={
            autocompleteEnabled
              ? 'Autocomplete suggestions are enabled.'
              : 'Autocomplete suggestions are disabled.'
          }
          placement='top'
        >
          <FormControlLabel
            control={
              <Switch
                checked={autocompleteEnabled}
                onChange={handleAutocompleteToggle}
                size='small'
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#232536',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#232536',
                  },
                }}
              />
            }
            label='Autocomplete'
            labelPlacement='start'
            sx={{
              ml: { xs: 0, sm: 1 },
              mr: { xs: 0, sm: 1 },
              mb: { xs: 1, sm: 0 },
              alignSelf: { xs: 'flex-end', sm: 'center' },
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#232536',
                mr: 1,
              },
            }}
          />
        </Tooltip>
      </Box>

      <Box>
        {subTabValue === SUB_TAB_INDEX.TAX_LAWS && (
          <GlobalSearchInLawCSP
            useExactSearch
            autocompleteEnabled={autocompleteEnabled}
          />
        )}
        {subTabValue === SUB_TAB_INDEX.TAX_TRANSPARENCY && (
          <GlobalSearchInTaxTransparencyCSP
            useExactSearch
            autocompleteEnabled={autocompleteEnabled}
          />
        )}
        {subTabValue === SUB_TAB_INDEX.TAX_TREATIES && (
          <TaxTreatyList
            useExactSearch
            autocompleteEnabled={autocompleteEnabled}
          />
        )}
      </Box>
    </Box>
  )
}

export default ExactSearchTab
